import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type {
  AdminSubscription,
  AdminTrial,
  CreateFreeTrialInput,
  TrialBundle,
  ListSubscriptionsQuery,
  ListTrialsQuery,
  Paginated,
  Session,
  SwapTutorResult,
  TrialPipeline,
} from '../types/api';

// ----------------------------------------------------------------- trials --

export function useTrialPipeline() {
  return useQuery({
    queryKey: qk.trialPipeline,
    queryFn: () => api.get<TrialPipeline>('/admin/trials/pipeline'),
    // The queue is a shared worklist — two people clearing it should not be
    // looking at different numbers for a minute.
    refetchInterval: 30_000,
  });
}

export function useTrials(query: ListTrialsQuery) {
  return useQuery({
    queryKey: qk.trials(query),
    queryFn: () => api.get<Paginated<AdminTrial>>('/admin/trials', query),
    placeholderData: (prev) => prev,
  });
}

export function useTrial(id: string | undefined) {
  return useQuery({
    queryKey: qk.trial(id ?? ''),
    queryFn: () => api.get<AdminTrial>(`/admin/trials/${id}`),
    enabled: Boolean(id),
  });
}

export interface AssignTrialInput {
  id: string;
  tutorId: string;
  /** One of these, never both — the API refuses the pair. */
  slotId?: string;
  startTime?: string;
  durationMinutes?: number;
}

export function useAssignTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AssignTrialInput) =>
      api.post<AdminTrial>(`/admin/trials/${id}/assign`, body),
    onSuccess: (trial) => {
      // An assignment moves the trial between two pipeline columns and puts
      // a lesson on the calendar, so all three lists are now stale.
      void qc.invalidateQueries({ queryKey: qk.trialsAll });
      void qc.invalidateQueries({ queryKey: qk.trialPipeline });
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      qc.setQueryData(qk.trial(trial.id), trial);
    },
  });
}

export function useCancelTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<AdminTrial>(`/admin/trials/${id}/cancel`, { reason }),
    onSuccess: (trial) => {
      void qc.invalidateQueries({ queryKey: qk.trialsAll });
      void qc.invalidateQueries({ queryKey: qk.trialPipeline });
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      qc.setQueryData(qk.trial(trial.id), trial);
    },
  });
}

// ---------------------------------------------------------- subscriptions --

export function useSubscriptions(query: ListSubscriptionsQuery) {
  return useQuery({
    queryKey: qk.subscriptions(query),
    queryFn: () => api.get<Paginated<AdminSubscription>>('/admin/subscriptions', query),
    placeholderData: (prev) => prev,
  });
}

export function useSubscription(id: string | undefined) {
  return useQuery({
    queryKey: qk.subscription(id ?? ''),
    queryFn: () => api.get<AdminSubscription>(`/admin/subscriptions/${id}`),
    enabled: Boolean(id),
  });
}

export interface SwapTutorInput {
  id: string;
  tutorId: string;
  reason?: string;
  reassignScheduled?: boolean;
}

export function useSwapTutor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SwapTutorInput) =>
      api.post<SwapTutorResult>(`/admin/subscriptions/${id}/swap-tutor`, body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: qk.subscriptionsAll });
      // Upcoming lessons may have moved to the new tutor.
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
      qc.setQueryData(qk.subscription(result.subscription.id), result.subscription);
    },
  });
}

export interface ScheduleLessonInput {
  id: string;
  slotId?: string;
  startTime?: string;
  durationMinutes?: number;
  subjectId?: string;
  tutorId?: string;
}

export function useScheduleLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ScheduleLessonInput) =>
      api.post<Session>(`/admin/subscriptions/${id}/lessons`, body),
    onSuccess: (_session, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.subscription(id) });
      void qc.invalidateQueries({ queryKey: qk.subscriptionsAll });
      void qc.invalidateQueries({ queryKey: qk.adminSessionsAll });
    },
  });
}

export function useTrialBundles() {
  return useQuery({ queryKey: qk.trialBundles, queryFn: () => api.get<TrialBundle[]>('/bundles') });
}

export function useCreateFreeTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFreeTrialInput) => api.post<AdminTrial>('/admin/trials/free', body),
    onSuccess: async (trial) => {
      qc.setQueryData(qk.trial(trial.id), trial);
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.trialsAll }),
        qc.invalidateQueries({ queryKey: qk.trialPipeline }),
        qc.invalidateQueries({ queryKey: qk.adminSessionsAll }),
        qc.invalidateQueries({ queryKey: qk.userSessions(trial.student.id) }),
        qc.invalidateQueries({ queryKey: qk.auditAll }),
        qc.invalidateQueries({ queryKey: qk.stats }),
      ]);
    },
  });
}
